import React from 'react';
import styled, { ThemeContext } from 'styled-components';
import { SCREEN_SIZES } from '@constants/screen';

/** Keys consumed only for CSS on Wrapper — must not reach the DOM */
const TRANSIENT_TEXT_STYLE_KEYS = new Set([
  'lineHeight',
  'letterSpacing',
  'fontSize',
  'thin',
  'fontWeight',
  'fontStyle',
  'opacity',
  'textAlign',
  'align',
  'textTransform',
  'userSelect',
  'cursor',
  'fontFamily',
  'color',
]);

const mapToTransientStyleProps = (source) => {
  const out = {};
  Object.keys(source || {}).forEach((key) => {
    const val = source[key];
    if (val === undefined) {
      return;
    }
    if (TRANSIENT_TEXT_STYLE_KEYS.has(key)) {
      out[`$${key}`] = val;
    } else {
      out[key] = val;
    }
  });
  return out;
};

const Wrapper = styled.span`
  box-sizing: border-box;
  line-height: 160%;

  ${(props) => (props.$lineHeight ? `line-height: ${props.$lineHeight};` : '')};
  ${(props) => (props.$letterSpacing ? `letter-spacing: ${props.$letterSpacing};` : '')};
  ${(props) => (props.$fontSize ? `font-size: ${props.$fontSize};` : '')};
  ${(props) =>
    props.$bold
      ? 'font-weight: 800;'
      : props.$thin
      ? 'font-weight: 100;'
      : props.$fontWeight
      ? `font-weight: ${props.$fontWeight};`
      : ''};
  ${(props) => (props.$fontStyle ? `font-style: ${props.$fontStyle};` : '')};

  ${(props) => (props.$opacity ? `opacity: ${props.$opacity};` : '')};

  ${(props) => (props.$textAlign ? `text-align: ${props.$align || props.$textAlign};` : '')};
  ${(props) => (props.$textTransform ? `text-transform: ${props.$textTransform};` : '')};
  ${(props) => (props.$userSelect ? `user-select: ${props.$userSelect};` : '')};
  ${(props) => (props.$cursor ? `cursor: ${props.$cursor};` : '')};
  ${(props) =>
    props.$truncate ? 'display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;' : ''};

  color: ${({ $color, $isLowContrast, theme }) =>
    $isLowContrast ? theme.themeColors.secondaryText : $color || theme.themeColors.text};
  ${(props) => (props.$fontFamily ? `font-family: ${props.$fontFamily};` : '')};

  * {
    color: ${({ $color, $isLowContrast, theme }) =>
      $isLowContrast ? theme.themeColors.secondaryText : $color || theme.themeColors.text};
    ${(props) => (props.$fontFamily ? `font-family: ${props.$fontFamily};` : '')};
    ${(props) => (props.$fontSize ? `font-size: ${props.$fontSize};` : '')};
    ${(props) => (props.$letterSpacing ? `letter-spacing: ${props.$letterSpacing};` : '')};
    ${(props) => (props.$lineHeight ? `line-height: ${props.$lineHeight};` : '')};
    ${(props) =>
      props.$truncate ? 'display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;' : ''};
  }
`;

export const BaseText = (props) => {
  const {
    isLowContrast,
    isSmall,
    isLarge,
    fontSettings: props_fontSettings,
    uppercase,
    center,
    bold: boldProp,
    truncate,
    textCase: textCaseProp,
    children,
    ...wrapperProps
  } = props;

  const theme = React.useContext(ThemeContext);
  const screenSize = theme?.screenSize || null;
  const isMobile = screenSize === SCREEN_SIZES.mobile;

  const fontSettings = props_fontSettings || { small: {}, standard: {} };
  const fontSettingsSource = (isSmall || isMobile) && !isLarge ? fontSettings.small : fontSettings.standard;
  const fontSettingsStyleProps = { ...fontSettingsSource };
  const textCaseFromFontSettings = fontSettingsStyleProps.textCase;
  delete fontSettingsStyleProps.textCase;
  const boldFromFontSettings = fontSettingsStyleProps.bold;
  delete fontSettingsStyleProps.bold;

  const bold = boldProp ?? boldFromFontSettings;
  const textCase = textCaseProp !== undefined ? textCaseProp : textCaseFromFontSettings;

  const transientAlias = {};
  if (uppercase) {
    transientAlias.$textTransform = 'uppercase';
  }
  if (center) {
    transientAlias.$textAlign = 'center';
  }
  if (textCase === 'uppercase') {
    transientAlias.$textTransform = 'uppercase';
  } else if (textCase === 'none') {
    transientAlias.$textTransform = 'none';
  }

  const wrapperForwardProps = {
    ...mapToTransientStyleProps({
      ...fontSettingsStyleProps,
      ...wrapperProps,
    }),
    ...transientAlias,
    $isLowContrast: isLowContrast,
    $truncate: truncate,
    $bold: bold,
  };

  TRANSIENT_TEXT_STYLE_KEYS.forEach((key) => {
    delete wrapperForwardProps[key];
  });

  if (bold) {
    return (
      <Wrapper className={'base-text'} {...wrapperForwardProps}>
        <b>{children}</b>
      </Wrapper>
    );
  }
  return (
    <Wrapper className={'base-text'} {...wrapperForwardProps}>
      {children}
    </Wrapper>
  );
};
